import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Virtual, Autoplay, Navigation, Keyboard } from "swiper";
import styled from "styled-components";

import "swiper/css";
import "swiper/css/virtual";
import "swiper/css/navigation";

/* ── Imagem do slide ─────────────────────────────── */
const Img = styled.img`
  max-height: 60vh;
  width: 100%;
  object-fit: contain;
  display: block;
  margin: 0 auto;

  @media (max-width: 600px) {
    max-height: 55vh;
    max-width: 100vw;
  }
`;

/* ── Wrapper com posicionamento relativo ─────────── */
const SliderWrapper = styled.div`
  position: relative;
  width: 100%;

  /* Esconde os botões nav e autoplay no celular */
  @media (max-width: 768px) {
    .slider-nav-btn,
    .slider-autoplay-btn {
      display: none !important;
    }
  }
`;

/* ── Botões de navegação (prev/next) ─────────────── */
const NavButton = styled.button.attrs({ className: "slider-nav-btn" })`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: rgba(255, 255, 255, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  &.prev { left: 12px; }
  &.next { right: 12px; }
`;

/* ── Botão de autoplay (discreto, canto inferior) ── */
const AutoplayButton = styled.button.attrs({ className: "slider-autoplay-btn" })`
  position: absolute;
  bottom: 12px;
  right: 14px;
  width: 48px;
  height: 48px;
  
  z-index: 10;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  padding: 4px 10px;
  font-size: 11px;
  letter-spacing: 0.04em;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: rgba(255, 255, 255, 0.8);
    transform: translateY(-5%) scale(1.1);
  }
`;

const Slider = ({ list }) => {
  const swiperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  if (!list || list.length === 0) {
    return (
      <div style={{ color: "white", textAlign: "center", padding: "20px" }}>
        Nenhuma foto encontrada.
      </div>
    );
  }

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const toggleAutoplay = () => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    if (isPlaying) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <SliderWrapper>
      {/* Botão Anterior */}
      <NavButton className="prev" onClick={handlePrev} aria-label="Slide anterior">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z" /></svg>
      </NavButton>

      {/* Botão Próximo */}
      <NavButton className="next" onClick={handleNext} aria-label="Próximo slide">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z" /></svg>
      </NavButton>

      {/* Toggle de Autoplay */}
      <AutoplayButton onClick={toggleAutoplay} aria-label="Pausar ou iniciar autoplay">
        {isPlaying ? <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M520-200v-560h240v560H520Zm-320 0v-560h240v560H200Zm400-80h80v-400h-80v400Zm-320 0h80v-400h-80v400Zm0-400v400-400Zm320 0v400-400Z" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M320-200v-560l440 280-440 280Zm80-280Zm0 134 210-134-210-134v268Z" /></svg>}
      </AutoplayButton>

      <Swiper
        modules={[Virtual, Autoplay, Navigation, Keyboard]}
        onSwiper={(swiper) => { swiperRef.current = swiper; }}
        virtual
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        speed={700}
        loop={true}
        grabCursor={true}
        keyboard={{ enabled: true }}
        style={{ width: "100%", height: "100%" }}
      >
        {list.map((url, index) => (
          <SwiperSlide key={url} virtualIndex={index}>
            <Img src={url} loading="lazy" alt={`slide-${index}`} />
          </SwiperSlide>
        ))}
      </Swiper>
    </SliderWrapper>
  );
};

export default Slider;